import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { useChat } from '../store';
import { Avatar } from './Avatar';
import { getSocket } from '../socket';
import type { ConversationListEntry } from '../types';

interface NewChatModalProps {
  onClose: () => void;
}

export function NewChatModal({ onClose }: NewChatModalProps) {
  const users = useChat((s) => s.users);
  const me = useChat((s) => s.me);
  const upsertConversation = useChat((s) => s.upsertConversation);
  const selectConversation = useChat((s) => s.selectConversation);
  const setConversations = useChat((s) => s.setConversations);
  const activeChannelId = useChat((s) => s.activeChannelId);
  const conversations = useChat((s) => s.conversations);

  const [filter, setFilter] = useState('');
  const [mode, setMode] = useState<'dm' | 'group' | 'broadcast'>('dm');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [broadcastName, setBroadcastName] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const otherUsers = useMemo(
    () => users.filter((u) => u.id !== me?.id),
    [users, me?.id],
  );

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return otherUsers;
    return otherUsers.filter(
      (u) =>
        u.email.toLowerCase().includes(f) ||
        (u.displayName ?? '').toLowerCase().includes(f),
    );
  }, [filter, otherUsers]);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function startDm(participantId: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.createDm(participantId);
      // If we don't already have it in the list, add a stub entry.
      const existing = conversations.find((c) => c.id === res.conversation.id);
      if (!existing) {
        const stub: ConversationListEntry = {
          ...res.conversation,
          lastMessage: null,
          unreadCount: 0,
          lastReadId: null,
          myRole: 'room_admin',
          isFavorite: false,
          archivedAt: null,
          mutedUntil: null,
        channelId: activeChannelId,
        };
        upsertConversation(stub);
        await api
          .moveConversationToChannel(res.conversation.id, activeChannelId)
          .catch(() => {});
      }
      // Subscribe to the WS room for this conversation.
      getSocket().emit('conversation:join', { conversationId: res.conversation.id });
      selectConversation(res.conversation.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start chat');
    } finally {
      setBusy(false);
    }
  }

  async function startGroup() {
    if (selectedIds.length < 2) {
      setError('Pick at least 2 people for a group.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.createGroup(selectedIds, groupName.trim() || null);
      // New chats are filed under the default channel server-side; re-file to
      // where we're creating it (null = no channel = DMs).
      await api
        .moveConversationToChannel(res.conversation.id, activeChannelId)
        .catch(() => {});
      // Creator of a group is the owner — server returns the same role
      // on next conv-list refresh, but the optimistic stub mirrors that
      // here so the Info panel can show the owner badge immediately.
      const stub: ConversationListEntry = {
        ...res.conversation,
        lastMessage: null,
        unreadCount: 0,
        lastReadId: null,
        myRole: 'room_owner',
        isFavorite: false,
        archivedAt: null,
        mutedUntil: null,
        channelId: activeChannelId,
      };
      upsertConversation(stub);
      // New conversations are filed under the default channel server-side; refetch
      // so the row shows under its channel (the stub alone is channel-less).
      void api.conversations().then((r) => setConversations(r.conversations));
      getSocket().emit('conversation:join', { conversationId: res.conversation.id });
      selectConversation(res.conversation.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setBusy(false);
    }
  }

  function toggle(id: string) {
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  }

  async function startBroadcast() {
    if (!broadcastName.trim()) {
      setError('Give the list a name.');
      return;
    }
    if (selectedIds.length === 0) {
      setError('Pick at least one recipient.');
      return;
    }
    if (!broadcastBody.trim()) {
      setError("Type a message — broadcasts can't be empty.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.createBroadcast(
        broadcastName.trim(),
        selectedIds,
      );
      const result = await api.sendBroadcast(created.broadcast.id, broadcastBody.trim());
      if (result.failed > 0 && result.sent === 0) {
        setError("None of the messages went through. Check Privacy → Calls/Groups audiences on your recipients, or try again.");
        return;
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send broadcast');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl flex flex-col max-h-[80vh]"
      >
        <div className="px-5 py-4 border-b border-zinc-900">
          <h2 className="text-base font-semibold mb-3">New chat</h2>
          <div className="flex gap-1.5 text-xs">
            <button
              onClick={() => {
                setMode('dm');
                setSelectedIds([]);
              }}
              className={`px-3 py-1 rounded-md transition ${
                mode === 'dm'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300'
              }`}
            >
              Direct message
            </button>
            <button
              onClick={() => setMode('group')}
              className={`px-3 py-1 rounded-md transition ${
                mode === 'group'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300'
              }`}
            >
              Group
            </button>
            <button
              onClick={() => {
                setMode('broadcast');
                setSelectedIds([]);
                setError(null);
              }}
              className={`px-3 py-1 rounded-md transition ${
                mode === 'broadcast'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300'
              }`}
            >
              Broadcast
            </button>
          </div>
        </div>

        {mode === 'group' && (
          <div className="px-5 pt-3">
            <label className="block text-xs text-zinc-400 mb-1">Group name (optional)</label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Project Phoenix"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}

        {mode === 'broadcast' && (
          <div className="px-5 pt-3 space-y-2">
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Sends one message to each recipient as a separate DM from you.
              Recipients see a normal direct chat — they don't see each other
              and can reply privately.
            </p>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">List name</label>
              <input
                type="text"
                value={broadcastName}
                onChange={(e) => setBroadcastName(e.target.value)}
                placeholder="e.g. All-hands updates"
                maxLength={100}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Message</label>
              <textarea
                value={broadcastBody}
                onChange={(e) => setBroadcastBody(e.target.value)}
                placeholder="What do you want to send?"
                rows={3}
                maxLength={8000}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-b border-zinc-900">
          <input
            type="text"
            autoFocus
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search teammates…"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="text-center text-xs text-zinc-500 py-6">No matches.</p>
          )}
          {filtered.map((u) => {
            const checked = selectedIds.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => (mode === 'dm' ? startDm(u.id) : toggle(u.id))}
                disabled={busy}
                className={`w-full px-5 py-2 flex items-center gap-3 hover:bg-zinc-900 transition disabled:opacity-50 ${
                  mode !== 'dm' && checked ? 'bg-zinc-900' : ''
                }`}
              >
                <Avatar
                  id={u.id}
                  displayName={u.displayName}
                  email={u.email}
                  online={u.online}
                  size={32}
                />
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm truncate">{u.displayName ?? u.email}</p>
                  {u.displayName && (
                    <p className="text-[10px] text-zinc-500 truncate">{u.email}</p>
                  )}
                </div>
                {mode !== 'dm' && (
                  <span
                    className={`w-4 h-4 rounded border ${
                      checked ? 'bg-indigo-500 border-indigo-500' : 'border-zinc-700'
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <p className="px-5 py-2 text-xs text-rose-400 border-t border-zinc-900">{error}</p>
        )}

        {mode === 'group' && (
          <div className="px-5 py-3 border-t border-zinc-900 flex justify-between items-center">
            <span className="text-xs text-zinc-400">
              {selectedIds.length} selected
            </span>
            <button
              onClick={startGroup}
              disabled={busy || selectedIds.length < 2}
              className="rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white transition"
            >
              {busy ? 'Creating…' : 'Create group'}
            </button>
          </div>
        )}

        {mode === 'broadcast' && (
          <div className="px-5 py-3 border-t border-zinc-900 flex justify-between items-center">
            <span className="text-xs text-zinc-400">
              {selectedIds.length} {selectedIds.length === 1 ? 'recipient' : 'recipients'}
            </span>
            <button
              onClick={startBroadcast}
              disabled={
                busy ||
                selectedIds.length === 0 ||
                !broadcastName.trim() ||
                !broadcastBody.trim()
              }
              className="rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white transition"
            >
              {busy ? 'Sending…' : 'Send broadcast'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
